import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Check, Gift, Star, CheckCircle2 } from 'lucide-react';
import API from '../services/api';

const iconifyUrl = (name, color) =>
  `https://api.iconify.design/${name}.svg?color=${encodeURIComponent(color)}`;

const Abonnements = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [abonnementActuel, setAbonnementActuel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    API.get('/abonnements/plans').then(res => setPlans(res.data.plans));
    if (user) {
      API.get('/abonnements/mon-abonnement').then(res => setAbonnementActuel(res.data.abonnement));
    }
  }, [user]);

  const handleSouscrire = async (planId) => {
    if (!user) { navigate('/connexion'); return; }
    setLoading(true);
    setMessage('');
    try {
      const res = await API.post('/abonnements/souscrire', { plan: planId });
      setMessage('✅ ' + res.data.message);
      setAbonnementActuel(res.data.abonnement);
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.message || 'Erreur'));
    } finally {
      setLoading(false);
    }
  };

  const planConfig = {
    starter: { color: '#3b82f6', icon: 'ph:rocket-launch-duotone', label: 'Starter' },
    business: { color: '#8b5cf6', icon: 'ph:buildings-duotone', label: 'Business' },
    premium: { color: '#F5A623', icon: 'ph:crown-duotone', label: 'Premium' }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Bannière promo */}
      <div style={{ background: 'linear-gradient(90deg, #16a34a, #22c55e)' }}
        className="text-white text-center py-2 px-4 text-sm font-bold flex items-center justify-center gap-2">
        <Gift size={16} />
        <span>Période promotionnelle — Tous les abonnements à <span className="font-black">0 FCFA</span> jusqu'au 30 septembre</span>
      </div>

      {/* Header noir */}
      <div className="py-16 px-4 text-center relative overflow-hidden" style={{ background: '#0A0A0A' }}>
        <div style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '600px', height: '2px',
          background: 'linear-gradient(90deg, transparent, #F5A623, transparent)'
        }} />
        <div style={{ color: '#F5A623' }} className="text-sm font-bold tracking-widest uppercase mb-3 flex items-center justify-center gap-2">
          Plans professionnels
        </div>
        <h1 className="text-white text-4xl font-black mb-4">
          Boostez votre <span style={{ color: '#F5A623' }}>activité</span>
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Choisissez le plan qui correspond à vos besoins et développez votre présence sur JJ Cars
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12 pb-16">

        {message && (
          <div className={`p-4 rounded-xl mb-8 text-center font-semibold ${
            message.startsWith('✅')
              ? 'bg-green-50 border border-green-300 text-green-600'
              : 'bg-red-50 border border-red-300 text-red-600'
          }`}>
            {message}
          </div>
        )}

        {/* Abonnement actuel - ✅ BOUTON "ANNULER" RETIRÉ */}
        {abonnementActuel && abonnementActuel.statut === 'actif' && (
          <div style={{ background: '#fffbeb', border: '1px solid #F5A623' }}
            className="rounded-2xl p-5 mb-10">
            <div>
              <p style={{ color: '#F5A623' }} className="font-black text-lg flex items-center gap-2">
                <img src={iconifyUrl('ph:crown-duotone', '#F5A623')} alt="" className="w-5 h-5" /> Abonnement actif : {abonnementActuel.plan.toUpperCase()}
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Expire le : {new Date(abonnementActuel.date_fin).toLocaleDateString('fr-FR')}
              </p>
              <p className="text-gray-400 text-xs mt-2">
                💡 Pour annuler votre abonnement, contactez l'administrateur
              </p>
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Plan gratuit */}
        <div
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
          className="rounded-2xl overflow-hidden transition hover:shadow-lg"
        >
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div style={{ background: '#f3f4f615', border: '1px solid #e5e7eb' }}
                className="w-10 h-10 rounded-xl flex items-center justify-center">
                <img src={iconifyUrl('ph:seal-check-duotone', '#9ca3af')} alt="" className="w-5 h-5" />
              </div>
              <h2 className="text-gray-900 font-black text-lg">Plan Gratuit</h2>
            </div>

            {/* Prix */}
            <div className="mb-6">
              <span className="text-4xl font-black text-gray-900">0</span>
              <span className="text-gray-400 ml-1 text-sm">FCFA/mois</span>
            </div>

            {/* Fonctionnalités */}
            <ul className="space-y-3 mb-8">
              {[
                '10 annonces actives maximum',
                '5 photos par annonce',
                'Validité : 30 jours',
                'Renouvellement manuel gratuit',
                'Messagerie interne',
                'Pas de mise en avant'
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <div style={{ background: '#f3f4f6' }} className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-gray-400 text-xs">✓</span>
                  </div>
                  <span className="text-gray-500">{f}</span>
                </li>
              ))}
            </ul>

            {/* Bouton */}
            <button
              disabled
              className="w-full py-3 rounded-xl font-black transition bg-gray-100 text-gray-400 cursor-default"
            >
              Plan actuel par défaut
            </button>
          </div>
        </div>
          {plans.map((plan, index) => {
            const config = planConfig[plan.id];
            const iconName = config?.icon || 'ph:crown-duotone';
            const isPopular = index === 1;
            const isActuel = abonnementActuel?.plan === plan.id && abonnementActuel?.statut === 'actif';

            return (
              <div
                key={plan.id}
                style={{
                  background: '#fff',
                  border: `1px solid ${isPopular ? '#F5A623' : '#e5e7eb'}`,
                  transform: isPopular ? 'scale(1.03)' : 'scale(1)',
                  boxShadow: isPopular ? '0 8px 30px rgba(245,166,35,0.15)' : '0 2px 8px rgba(0,0,0,0.06)'
                }}
                className="rounded-2xl overflow-hidden transition hover:shadow-lg"
              >
                {isPopular && (
                  <div style={{ background: '#F5A623' }} className="text-black text-center py-2 text-sm font-black flex items-center justify-center gap-1">
                    <Star size={14} fill="currentColor" /> LE PLUS POPULAIRE
                  </div>
                )}

                <div className="p-6">
                  {/* Icône et nom */}
                  <div className="flex items-center gap-3 mb-4">
                    <div style={{ background: config?.color + '15', border: `1px solid ${config?.color}30` }}
                      className="w-10 h-10 rounded-xl flex items-center justify-center">
                      <img src={iconifyUrl(iconName, config?.color)} alt="" className="w-5 h-5" />
                    </div>
                    <h2 className="text-gray-900 font-black text-lg">{plan.nom}</h2>
                  </div>

                  {/* Prix - Promo jusqu'au 30 septembre 2026 */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span style={{ color: '#F5A623' }} className="text-4xl font-black">0</span>
                      <span className="text-gray-400 text-sm">FCFA/mois</span>
                      <span className="text-gray-400 line-through text-base">
                        {plan.prix.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <span style={{ background: '#F5A62315', color: '#F5A623' }}
                      className="inline-block text-xs font-bold px-2 py-0.5 rounded-full">
                      Promo jusqu'au 30 septembre
                    </span>
                  </div>

                  {/* Fonctionnalités */}
                  <ul className="space-y-3 mb-8">
                    {plan.fonctionnalites.map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <div style={{ background: '#F5A62315' }} className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check size={12} style={{ color: '#F5A623' }} />
                        </div>
                        <span className="text-gray-600">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Bouton */}
                  <button
                    onClick={() => handleSouscrire(plan.id)}
                    disabled={loading || isActuel}
                    style={{
                      background: isActuel ? '#f3f4f6' : isPopular ? '#F5A623' : 'transparent',
                      border: isActuel ? 'none' : isPopular ? 'none' : `1px solid ${config?.color}`,
                      color: isActuel ? '#9ca3af' : isPopular ? '#000' : config?.color
                    }}
                    className="w-full py-3 rounded-xl font-black transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isActuel ? (<><CheckCircle2 size={16} /> Plan actuel</>) : loading ? 'Traitement...' : 'Choisir ce plan'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Abonnements;
